import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { isUniversityBeaconManager, useCurrentProvider } from './Components/Permissions';
import { Activity } from './Pages/Activity/Activity';
import { AppointmentsPage } from './Pages/Appointments';
import { Booking } from './Pages/Booking/BookingWizard';
import { SelfPayReferralBookingWizard } from './Pages/Booking/SelfPayReferralBooking/SelfPayReferralWizard';
import { CampusAnnouncementsFromParams } from './Pages/CampusAnnouncements/CampusAnnouncements';
import { ConfigPage } from './Pages/Config/ConfigPage';
import { ConnectNowCallReports } from './Pages/ConnectNowCallReports/ConnectNowCallReports';
import { CounselorDashboard } from './Pages/CounselorDashboard';
import { DdhCrisisReport } from './Pages/DdhCrisisReports/DdhCrisisReports';
import { DebugPage } from './Pages/Debug';
import { HandbookFromParams } from './Pages/Handbook/Handbook';
import { Home } from './Pages/Home';
import { IopPage } from './Pages/Iop/Iop';
import { MessagesPage } from './Pages/Messages/MessagesPage';
import { NeedsOutreachPage } from './Pages/NeedsOutreach';
import { CreateOrganization } from './Pages/Organizations/Create';
import { OrganizationAdmin } from './Pages/Organizations/OrganizationAdmin';
import { OrganizationMantraProvider } from './Pages/Organizations/OrganizationMantraProvider';
import { Organizations } from './Pages/Organizations/Organizations';
import { ProviderPage } from './Pages/Provider';
import { NewProvider } from './Pages/Provider/NewProvider';
import { ProviderList, UniStaffList } from './Pages/Provider/ProviderList';
import { ProviderNetwork } from './Pages/ProviderNetwork/ProviderNetwork';
import { ProviderSettings } from './Pages/ProviderSettings';
import { ReferralsPage } from './Pages/Referrals/ReferralsPage';
import { Dashboard } from './Pages/Reporting/Dashboard';
import { ChangePassword, ForgotPassword, Login } from './Pages/Session';
import { PasswordlessHandler } from './Pages/Session/PasswordlessHandler';
import { SetPassword } from './Pages/Session/SetPassword';
import { Unsubscribe } from './Pages/Session/Unsubscribe';
import { AddStaffPage } from './Pages/Staff/AddStaffPage';
import { StaffPage } from './Pages/Staff/StaffPage';
import { StudentsPage } from './Pages/Students';
import { EnrollSelection } from './Pages/Students/EnrollSelection';
import { EnrollOptionCharlieHealth } from './Pages/Students/EnrollSelection/EnrollOptionCharlieHealth';
import { EnrollTogetherAll } from './Pages/Students/EnrollSelection/EnrollTogetherall';
import { TasksPage } from './Pages/Tasks';
import { TermsOfUse } from './Pages/Terms';
import { Tutorial } from './Pages/Tutorial/Tutorial';
import { UsersPage } from './Pages/UserList/UsersPage';
import { SuggestedProviders } from './Pages/Users/SuggestedProviders/SuggestedProviders';
import { UserDrilldown } from './Pages/Users/UserDrilldown';
import { MCPUtilizationPage } from './Pages/Utilization/MCPUtilizationPage';
import { VerificationPage } from './Pages/Verification';
import { Permission } from './graphQL';
import { BeaconPage } from './Pages/Beacon/BeaconPage';
import { useShowBeaconNavItem } from './Pages/Beacon/hooks/useShowBeaconNavItem';

export const PublicRoutes: React.FC = ({ children }) => (
  <Switch>
    <Route path="/login" component={Login} />
    <Route path="/forgotPassword" component={ForgotPassword} />
    <Route path="/setPassword" component={SetPassword} />
    {/* 
      /resetPassword is deprecated, replaced by /setPassword
      We are keeping it temporarily in case users have saved the link and try to access it
    */}
    <Route path="/resetPassword" component={SetPassword} />
    <Route path="/passwordless" component={PasswordlessHandler} />
    <Route path="/unsubscribe" component={Unsubscribe} />
    <Route exact path="/changePassword" component={ChangePassword} />
    {children}
  </Switch>
);

export const AuthenticatedRoutes = () => (
  <Switch>
    <Route exact path="/debug/:id?" component={DebugPage} />
    <Route exact path="/users" component={UsersPage} />
    <Route exact path="/usersearch" component={UsersPage} />
    <Route exact path="/users/:id" component={UserDrilldown} />
    <Route exact path="/needs-outreach" component={ProtectedNeedsOutreach} />
    <Route exact path="/providers" component={ProviderList} />
    <Route exact path="/providers/uni" component={UniStaffList} />
    <Route exact path="/providers/new" component={NewProvider} />
    <Route path="/providers/:id" component={ProviderPage} />
    <Route exact path="/odes-reports" component={ProtectedConnectNowCallReports} />
    <Route exact path="/connectnow-reports" component={ProtectedConnectNowCallReports} />
    <Route exact path="/activity" component={ProtectedActivity} />
    <Route exact path="/crisisreports" component={ProtectedDdhCrisisReport} />
    <Route exact path="/messages" component={MessagesPage} />
    <Route path="/appointments" component={AppointmentsPage} />
    <Route path="/verification" component={ProtectedVerification} />
    <Route path="/book" component={Booking} />
    <Route path="/home" component={Home} />
    <Route path="/tasks" component={TasksPage} />
    <Route path="/settings" component={ProviderSettings} />
    <Route exact path="/organizations" component={ProtectedOrganizations} />
    <Route
      exact
      path="/organizations/:organizationId/admin"
      component={ProtectedOrganizationAdmin}
    />
    <Route
      exact
      path="/organizations/:organizationId/provider"
      component={OrganizationMantraProvider}
    />
    <Route exact path="/organizations/:organizationId/students" component={StudentsPage} />
    <Route
      exact
      path="/organizations/:organizationId/referrals"
      component={ProtectedReferralsPage}
    />
    <Route
      exact
      path="/organizations/:organizationId/students/enroll"
      component={ProtectedEnrollSelection}
    />
    <Route
      exact
      path="/organizations/:organizationId/students/enroll-option-iop"
      component={ProtectedEnrollOptionCharlieHealth}
    />
    <Route exact path="/beacon" component={ProtectedBeaconPage} />
    <Route
      exact
      path="/organizations/:organizationId/students/enroll-togetherall"
      component={ProtectedEnrollTogetherAll}
    />
    <Route exact path="/organizations/:organizationId/staff" component={StaffPage} />
    <Route exact path="/organizations/staff/add" component={ProtectedAddStaffPage} />
    <Route exact path="/organizations/:organizationId/handbook" component={HandbookFromParams} />
    <Route
      exact
      path="/organizations/:organizationId/updates"
      component={CampusAnnouncementsFromParams}
    />
    <Route exact path="/organizations/create" component={ProtectedCreateOrganization} />
    <Route exact path="/students" component={StudentsRedirect} />
    <Route exact path="/referrals" component={ReferralsRedirect} />
    <Route exact path="/staff" component={StaffRedirect} />
    <Route exact path="/organizations/:organizationId/dashboard" component={CounselorDashboard} />
    <Route exact path="/dashboard" component={DashboardRedirect} />
    <Route exact path="/organizations/:organizationId/reporting" component={ProtectedDashboard} />
    <Route exact path="/reporting" component={ReportingRedirect} />
    <Route
      exact
      path="/organizations/:organizationId/utilization"
      component={ProtectedMCPUtilization}
    />
    <Route exact path="/utilization" component={UtilizationRedirect} />
    <Route exact path="/config" component={ProtectedConfigPage} />
    <Route exact path="/terms" component={TermsOfUse} />
    <Route exact path="/provider-network/suggested-providers" component={SuggestedProviders} />
    <Route exact path="/provider-network" component={ProviderNetwork} />
    <Route exact path="/provider-network/book" component={SelfPayReferralBookingWizard} />
    <Route exact path="/tutorial" component={Tutorial} />
    <Route path="/iop" component={IopPage} />
    <Route path="*" component={CatchAll} />
  </Switch>
);

const OrganizationRedirect = ({ path }: { path: string }) => {
  const { currentProvider } = useCurrentProvider();
  // someday will need a UX for org selection
  const organizationId = currentProvider.organizations[0].id.toString();
  const pathWithOrgId = path.replace(':organizationId', organizationId);
  return <Redirect to={pathWithOrgId} />;
};

const StudentsRedirect = () => (
  <OrganizationRedirect path="/organizations/:organizationId/students" />
);
const ReferralsRedirect = () => (
  <OrganizationRedirect path="/organizations/:organizationId/referrals" />
);
const StaffRedirect = () => <OrganizationRedirect path="/organizations/:organizationId/staff" />;
const DashboardRedirect = () => (
  <OrganizationRedirect path="/organizations/:organizationId/dashboard" />
);
const ReportingRedirect = () => (
  <OrganizationRedirect path="/organizations/:organizationId/reporting" />
);
const UtilizationRedirect = () => (
  <OrganizationRedirect path="/organizations/:organizationId/utilization" />
);

const CatchAll = () => {
  const { appView, currentProvider } = useCurrentProvider();

  if (appView === 'oz') return <Redirect to="/home" />;
  if (appView === 'referral') return <Redirect to="/students" />;
  if (isUniversityBeaconManager(currentProvider)) {
    return <Redirect to="/beacon" />;
  }

  // we are assuming that MCP users only have one org
  // const mcpOrg = currentProvider.organizations[0];

  /*
  As of 01/14/22 SuperAdmins should not have access to "/home", redirect to "/dashboard"
  if (isProviderSuperAdmin(currentProvider))
    return (
      <Redirect
        to={hasPermission(Permission.Reporting) ? '/home' : `/organizations/${mcpOrg.id}/students`}
      />
    );
  */
  return <Redirect to="/dashboard" />;
};

type HasPermissionsOpts = {
  perms: Permission[];
  redirect?: string;
  requireAll?: boolean;
};

function hasPermissionsHLC<T extends {}>(
  C: React.FC<T>,
  { perms, redirect, requireAll = true }: HasPermissionsOpts
) {
  return (props: T) => {
    const { hasPermission } = useCurrentProvider();
    const check = requireAll
      ? perms.some(p => !hasPermission(p)) // ALL required: block if any missing
      : !perms.some(p => hasPermission(p)); // ANY required: block if none present

    if (check) {
      return <Redirect to={redirect ?? '/'} />;
    }
    return <C {...props} />;
  };
}

type CustomValidatorOpts = {
  customValidator: () => boolean;
  redirect?: string;
};

function hasCustomPermissionsHLC<T extends {}>(
  C: React.FC<T>,
  { customValidator, redirect = '/' }: CustomValidatorOpts
) {
  return (props: T) => {
    if (!customValidator()) {
      return <Redirect to={redirect} />;
    }
    return <C {...props} />;
  };
}

// Hoisted to module level so component references are stable across re-renders.
// Calling hasPermissionsHLC inline in JSX creates a new component type each render,
// which causes React Router to unmount/remount when AuthenticatedRoutes re-renders.
const ProtectedNeedsOutreach = hasPermissionsHLC(NeedsOutreachPage, {
  perms: [Permission.MantraAdmin],
});
const ProtectedConnectNowCallReports = hasPermissionsHLC(ConnectNowCallReports, {
  perms: [Permission.CrisisReportsViewAny, Permission.CrisisReportsViewOwnorg],
  requireAll: false,
});
const ProtectedActivity = hasPermissionsHLC(Activity, { perms: [Permission.MantraAdmin] });
const ProtectedDdhCrisisReport = hasPermissionsHLC(DdhCrisisReport, {
  perms: [Permission.CrisisReportsLegacyViewOwnorg],
});
const ProtectedVerification = hasPermissionsHLC(VerificationPage, {
  perms: [Permission.MantraAdmin],
});
const ProtectedOrganizations = hasPermissionsHLC(Organizations, {
  perms: [Permission.MantraAdmin],
});
const ProtectedOrganizationAdmin = hasPermissionsHLC(OrganizationAdmin, {
  perms: [Permission.MantraAdmin],
});
const ProtectedEnrollSelection = hasPermissionsHLC(EnrollSelection, {
  perms: [Permission.PatientCreate],
});
const ProtectedEnrollOptionCharlieHealth = hasPermissionsHLC(EnrollOptionCharlieHealth, {
  perms: [Permission.PatientCreate],
});
const ProtectedEnrollTogetherAll = hasPermissionsHLC(EnrollTogetherAll, {
  perms: [Permission.PatientCreate],
});
const ProtectedAddStaffPage = hasPermissionsHLC(AddStaffPage, {
  perms: [Permission.ProviderCreate],
});
const ProtectedCreateOrganization = hasPermissionsHLC(CreateOrganization, {
  perms: [Permission.MantraAdmin],
});
const ProtectedDashboard = hasPermissionsHLC(Dashboard, { perms: [Permission.Reporting] });
const ProtectedMCPUtilization = hasPermissionsHLC(MCPUtilizationPage, {
  perms: [Permission.UtilizationView],
});
const ProtectedConfigPage = hasPermissionsHLC(ConfigPage, { perms: [Permission.MantraAdmin] });
const ProtectedBeaconPage = hasCustomPermissionsHLC(BeaconPage, {
  customValidator: useShowBeaconNavItem,
});
const ProtectedReferralsPage = hasPermissionsHLC(ReferralsPage, {
  perms: [Permission.ReferralsView],
});
